import { FileWithPreview } from "@contexts/index";
import {
  GetPhotoSizeDto,
  PriceRangeDto,
  GetCouponDto,
} from "src/services/generated-api";
import { CouponType } from "src/enums/coupon-type";
import { debugLog } from "src/utils/debug-log";
import { ladderForCurrency } from "src/utils/photo-size-ladder";

/**
 * Helper function to get pricing ladder based on currency
 */
export const getPricingLadder = (
  photoSize: GetPhotoSizeDto | undefined,
  currency: string
) => {
  if (!photoSize) {
    return null;
  }
  // Its own ladder or none: never the euro one for another currency (B4).
  return ladderForCurrency<PriceRangeDto>(photoSize, currency);
};

/**
 * Helper function to get default price based on currency
 */
export const getDefaultPrice = (
  photoSize: GetPhotoSizeDto | undefined,
  currency: string
) => {
  if (!photoSize) {
    return null;
  }
  if (currency == "eur") {
    return photoSize?.price;
  }
  if (photoSize?.priceCurrencies && currency in photoSize?.priceCurrencies) {
    return (photoSize?.priceCurrencies as Record<string, number>)[currency];
  }
  const currency_split = currency.split("-")[0];
  if (currency_split === "eur") {
    return photoSize?.price;
  }
  return null;
};

/**
 * Calculate the price for not-yet-uploaded files
 */
export const calculatePendingFilesPrice = (
  files: FileWithPreview[],
  photoSize: GetPhotoSizeDto | undefined,
  currency: string
): number => {
  if (!files.length || !photoSize) return 0;

  // Calculate total files to determine which ladder price to use
  const totalFilesCount = files.reduce((total, f) => total + (f.count || 1), 0);

  // First get the base price and pricing ladder based on currency
  // 1. Start with default values (EUR)
  let basePrice = photoSize.price || 0;
  // Its own ladder or none — the euro ladder's cents are not this currency's.
  const pricingLadder = ladderForCurrency<PriceRangeDto>(photoSize, currency) ?? [];

  // 2. Override with currency-specific values if available
  if (photoSize.priceCurrencies && currency in photoSize.priceCurrencies) {
    basePrice = (photoSize.priceCurrencies as Record<string, number>)[currency];
  }

  // Now that we have resolved the price and ladder, calculate the total

  let pricePerPhoto = basePrice;

  // Apply pricing ladder if available
  if (pricingLadder && pricingLadder.length > 0) {
    const ladderPrice = pricingLadder.find(
      (l: PriceRangeDto) =>
        l.minQuantity <= totalFilesCount && l.maxQuantity > totalFilesCount
    )?.pricePerPhoto;

    if (ladderPrice !== undefined) {
      pricePerPhoto = ladderPrice;
    }
  }
  debugLog(
    `Price per photo for ${pricePerPhoto} (quantity: ${totalFilesCount})`
  );
  return pricePerPhoto * totalFilesCount;
};

/**
 * Apply coupon discount to the price
 */
export const applyCouponDiscount = (
  totalPrice: number,
  coupon: GetCouponDto | null | undefined
): number => {
  if (!coupon) return totalPrice;

  if (coupon.type === CouponType.PERCENTAGE) {
    let discount = (totalPrice * coupon.discount) / 100;
    if (coupon.maximumDiscountAmount) {
      discount = Math.min(discount, coupon.maximumDiscountAmount);
    }
    return Math.round(totalPrice - discount);
  } else if (coupon.type === CouponType.FIXED) {
    return Math.round(totalPrice - coupon.discount);
  }

  return totalPrice;
};
